import type { EmailTemplate, User, WeeklyReport } from "@/types";

const API_BASE = "/api";

/**
 * JWT tokens are now stored in httpOnly cookies only.
 * This function is deprecated - JWT tokens are not accessible via JavaScript.
 */
const getAuthToken = (): string | null => {
  // JWT tokens are stored in httpOnly cookies, not accessible via JavaScript
  console.log(
    "[API] JWT tokens now use httpOnly cookies - not accessible via JavaScript",
  );
  return null;
};

/**
 * Create headers with JWT authentication and CSRF protection
 *
 * Includes X-Requested-With header for CSRF protection.
 * This header is required by the backend when Origin/Referer headers are not present.
 */
const getAuthHeaders = (
  additionalHeaders: Record<string, string> = {},
): Record<string, string> => {
  const headers: Record<string, string> = {
    "X-Requested-With": "XMLHttpRequest", // CSRF protection header
    ...additionalHeaders,
  };

  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Handle API response and check for auth errors
 */
const handleResponse = async <T>(
  response: Response,
  errorMessage: string,
): Promise<T> => {
  if (response.status === 401) {
    // Token expired or invalid - clear session data and redirect to login
    const data = await response.json().catch(() => ({}));
    if (data.code === "TOKEN_EXPIRED") {
      console.log("JWT token expired, clearing session");
      // JWT tokens are in httpOnly cookies (automatically cleared by logout endpoint)
      // Clear user data and any legacy tokens from localStorage
      localStorage.removeItem("jwt_token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    throw new Error(data.error || "Authentication required");
  }

  if (!response.ok) {
    throw new Error(errorMessage);
  }

  return response.json();
};

export const api = {
  // Reports
  getAllReports: async (email: string): Promise<WeeklyReport[]> => {
    const response = await fetch(`${API_BASE}/reports?email=${email}`, {
      headers: getAuthHeaders(),
      credentials: "include", // Required for cookie-based auth
    });
    return handleResponse(response, "Failed to fetch reports");
  },

  getUserReports: async (email: string): Promise<WeeklyReport[]> => {
    const response = await fetch(`${API_BASE}/reports?email=${email}`, {
      headers: getAuthHeaders(),
      credentials: "include", // Required for cookie-based auth
    });
    return handleResponse(response, "Failed to fetch user reports");
  },

  createReport: async (report: WeeklyReport): Promise<void> => {
    const response = await fetch(`${API_BASE}/reports`, {
      method: "POST",
      headers: getAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(report),
      credentials: "include", // Required for cookie-based auth
    });
    if (!response.ok) throw new Error("Failed to create report");
  },

  // Users
  getAllUsers: async (): Promise<User[]> => {
    const response = await fetch(`${API_BASE}/users`, {
      headers: getAuthHeaders(),
      credentials: "include", // Required for cookie-based auth
    });
    return handleResponse(response, "Failed to fetch users");
  },

  createUser: async (user: User): Promise<void> => {
    const response = await fetch(`${API_BASE}/users`, {
      method: "POST",
      headers: getAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(user),
      credentials: "include", // Required for cookie-based auth
    });
    if (!response.ok) throw new Error("Failed to create user");
  },

  // Email Template
  getEmailTemplate: async (email: string): Promise<EmailTemplate> => {
    const response = await fetch(`${API_BASE}/templates/${email}`, {
      headers: getAuthHeaders(),
      credentials: "include", // Required for cookie-based auth
    });
    return handleResponse(response, "Failed to fetch email template");
  },

  updateEmailTemplate: async (template: EmailTemplate): Promise<void> => {
    const response = await fetch(`${API_BASE}/templates`, {
      method: "POST",
      headers: getAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(template),
      credentials: "include", // Required for cookie-based auth
    });
    if (!response.ok) throw new Error("Failed to update email template");
  },

  // Auth
  refreshToken: async (): Promise<{ token: string }> => {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: getAuthHeaders(),
      credentials: "include", // Required for cookie-based auth
    });
    return handleResponse(response, "Failed to refresh token");
  },
};
